import api from './api';

/**
 * Task Validated Item Service
 * Service for managing validations of task items like user stories, features, etc.
 */
class TaskValidatedItemService {
  /**
   * Get all validated items for a project
   * @param {string} projectId - Project ID
   * @returns {Promise} - Promise resolving to validated items
   */
  async getProjectValidatedItems(projectId) {
    try {
      const response = await api.get(`/task-validated-items/project/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project validated items:', error);
      throw error;
    }
  }

  /**
   * Get all validated items for a task within a project
   * @param {string} projectId - Project ID
   * @param {string} taskId - Task ID
   * @returns {Promise} - Promise resolving to validated items
   */
  async getTaskValidatedItems(projectId, taskId) {
    try {
      const response = await api.get(`/task-validated-items/project/${projectId}/task/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task validated items:', error);
      throw error;
    }
  }

  /**
   * Get all validated items for a conversation
   * @param {string} conversationId - Conversation ID
   * @returns {Promise} - Promise resolving to validated items
   */
  async getConversationValidatedItems(conversationId) {
    try {
      const response = await api.get(`/task-validated-items/conversation/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation validated items:', error);
      throw error;
    }
  }

  /**
   * Save a validated item
   * @param {Object} validatedItem - Validated item object
   * @param {string} validatedItem.projectId - Project ID
   * @param {string} validatedItem.taskId - Task ID
   * @param {string} validatedItem.conversationId - Conversation ID
   * @param {string} validatedItem.validated_id - ID of the validated item
   * @param {string} validatedItem.feedback - Optional feedback
   * @returns {Promise} - Promise resolving to saved validated item
   */
  async saveValidatedItem(validatedItem) {
    try {
      // Convert camelCase field names to snake_case for backend compatibility
      const backendPayload = {
        project_id: validatedItem.projectId,
        task_id: validatedItem.taskId,
        conversation_id: validatedItem.conversationId,
        validated_id: validatedItem.validated_id,
        feedback: validatedItem.feedback,
        item_name: validatedItem.item_name
      };
      
      const response = await api.post('/task-validated-items', backendPayload);
      return response.data;
    } catch (error) {
      console.error('Error saving validated item:', error);
      throw error;
    }
  }

  /**
   * Delete a validated item
   * @param {string} id - Validated item ID
   * @returns {Promise} - Promise resolving to success message
   */
  async deleteValidatedItem(id) {
    try {
      const response = await api.delete(`/task-validated-items/item/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting validated item:', error);
      throw error;
    }
  }

  /**
   * Check if an item is validated
   * @param {string} projectId - Project ID
   * @param {string} taskId - Task ID
   * @param {string} conversationId - Conversation ID
   * @param {string} validated_id - ID of the item to check
   * @returns {Promise<boolean>} - Promise resolving to boolean indicating if item is validated
   */
  async isItemValidated(projectId, taskId, conversationId, validated_id) {
    try {
      const response = await this.getConversationValidatedItems(conversationId);
      if (response.success && response.data && response.data.length > 0) {
        return response.data.some(item => 
          item.project_id === projectId && 
          item.task_id === taskId && 
          item.validated_id === validated_id
        );
      }
      return false;
    } catch (error) {
      console.error('Error checking if item is validated:', error);
      return false;
    }
  }
}

// Export singleton instance
const taskValidatedItemService = new TaskValidatedItemService();
export default taskValidatedItemService;